// NodeNodelistModal.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useBaseTypeAccess from 'hooks/useBaseTypeAccess';

// Utilities
import { resolveCreateNodeModule } from 'utilities/createNodeModuleContext';
import { getSingularLabel } from 'utilities/Languages';
import { createNode as createNodeFromType } from 'utilities/NodeFactory';
import { resolveNodeModule } from 'utilities/nodeModule';
import {
  getRelationDisplayLines,
  normalizeRelationDisplayFields,
  resolveRelationDisplayFieldDefs,
} from 'utilities/RelationDisplayFields';
import { typeAllowsManualCreate } from 'utilities/typeCreatePolicy';

// Services
import { subscribeToNodesByDatabase } from 'services/nodes/NodeReadService';
import { createNode as createNodeDoc, deleteNodes } from 'services/nodes/NodeWriteService';

// CSS
import './NodeNodelistModal.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import RelationQuickAddFields from 'node/form/fields/nodelist/RelationQuickAddFields';
import {
  applyRelationQuickAddValues,
  buildEmptyRelationQuickAddValues,
  hasRelationQuickAddFieldValues,
} from 'node/form/fields/nodelist/relationQuickAdd';

const normalizeNodeKeys = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const seen = new Set();

  return list
    .map((entry) => String(entry || '').trim())
    .filter((entry) => {
      if (!entry || seen.has(entry)) return false;
      seen.add(entry);
      return true;
    });
};

const getNodeTitle = (node) => String(node?.title || '').trim() || 'Untitled node';

const buildSelectedMap = (value) => {
  const map = new Map();
  normalizeNodeKeys(value).forEach((key) => {
    map.set(key, true);
  });
  return map;
};

/**
 * NodeNodelistModal
 *
 * Multi-select Relation picker: existing records with display-field metadata,
 * plus an optional permission-gated Quick Add form.
 *
 * @param {string} fieldLabel - Label displayed in the modal header.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {object} node - Parent node used for base context.
 * @param {function} setOpen - Setter for modal open state.
 * @param {string} targetType - Allowed target node type key.
 * @param {string[]} [displayFields] - Relation display field names from admin.
 * @param {string[]} value - Current selected node keys.
 * @param {function} onChange - Called with the next selected node keys.
 *
 * @returns {ReactNode} The rendered selection modal.
 */
const NodeNodelistModal = ({
  fieldLabel,
  isOpen,
  node,
  setOpen,
  targetType,
  displayFields,
  value,
  onChange,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- CONTEXT ----- //

  const { getType } = useNodeTypes();
  const { activeBase } = useBase();
  const { user } = useUser();
  const { theme } = useTheme();
  const { canWriteType } = useBaseTypeAccess();

  // ----- STATE ----- //

  const [availableNodes, setAvailableNodes] = useState([]);
  const [draftError, setDraftError] = useState('');
  const [draftFieldValues, setDraftFieldValues] = useState({});
  const [draftTitle, setDraftTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [quickAddExpanded, setQuickAddExpanded] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [selectedMap, setSelectedMap] = useState(() => buildSelectedMap(value));

  // ----- REFS ----- //

  const wasOpenRef = useRef(false);

  const baseKey = node?.baseKey || activeBase?.key || null;

  // ----- MEMOS ----- //

  const typeDef = useMemo(() => {
    if (!targetType) return null;
    return getType(targetType) || null;
  }, [getType, targetType]);

  const targetSchemaFields = useMemo(() => (
    Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : []
  ), [typeDef]);

  const normalizedDisplayFields = useMemo(
    () => normalizeRelationDisplayFields(displayFields),
    [displayFields],
  );

  const displayFieldDefs = useMemo(
    () => resolveRelationDisplayFieldDefs(normalizedDisplayFields, targetSchemaFields),
    [normalizedDisplayFields, targetSchemaFields],
  );

  const singularLabel = useMemo(() => {
    return getSingularLabel(typeDef, targetType || 'Item', 'en') || 'Item';
  }, [targetType, typeDef]);

  const canQuickAdd = Boolean(
    targetType
    && baseKey
    && typeAllowsManualCreate(typeDef || targetType)
    && canWriteType(targetType, typeDef),
  );

  const trimmedDraftTitle = useMemo(() => String(draftTitle || '').trim(), [draftTitle]);

  const quickAddCollapsedLabel = useMemo(() => (
    `Add ${String(singularLabel || 'item').toLowerCase()}...`
  ), [singularLabel]);

  const sortedNodes = useMemo(() => {
    return (Array.isArray(availableNodes) ? availableNodes : [])
      .filter((entry) => String(entry?.key || '').trim())
      .sort((left, right) => getNodeTitle(left).localeCompare(getNodeTitle(right)));
  }, [availableNodes]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setDraftError('');
      setDraftFieldValues(buildEmptyRelationQuickAddValues(displayFieldDefs));
      setDraftTitle('');
      setIsSaving(false);
      setQuickAddExpanded(false);
      setSaveError('');
      setSelectedMap(buildSelectedMap(value));
    }

    wasOpenRef.current = isOpen;
  }, [displayFieldDefs, isOpen, value]);

  useEffect(() => {
    if (draftError) setQuickAddExpanded(true);
  }, [draftError]);

  // Keep draft value keys in sync when target schema/display fields resolve after open.
  useEffect(() => {
    if (!isOpen) return;
    setDraftFieldValues((prev) => {
      const empty = buildEmptyRelationQuickAddValues(displayFieldDefs);
      const next = { ...empty };
      Object.keys(empty).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(prev, key)) {
          next[key] = prev[key];
        }
      });
      return next;
    });
  }, [displayFieldDefs, isOpen]);

  useEffect(() => {
    if (!isOpen || !baseKey || !targetType) {
      setAvailableNodes([]);
      return undefined;
    }

    return subscribeToNodesByDatabase(
      { type: targetType, baseKey },
      (rows) => {
        setAvailableNodes(Array.isArray(rows) ? rows : []);
      }
    );
  }, [isOpen, targetType, baseKey]);

  // ----- HANDLERS ----- //

  /**
   * handleToggle
   *
   * Toggles selection for an existing related node.
   *
   * @param {object} item
   */
  const handleToggle = (item) => {
    const key = String(item?.key || '').trim();
    if (!key) return;

    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, true);
      }
      return next;
    });
  };

  /**
   * handleExpandQuickAdd
   *
   * Expands the sticky Quick Add row into the create form.
   */
  const handleExpandQuickAdd = () => {
    if (isSaving) return;
    setQuickAddExpanded(true);
  };

  /**
   * handleDraftFieldChange
   *
   * Updates one Quick Add display-field value.
   *
   * @param {string} fieldName
   * @param {unknown} nextValue
   */
  const handleDraftFieldChange = (fieldName, nextValue) => {
    const name = String(fieldName || '').trim();
    if (!name) return;
    setDraftFieldValues((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
    if (draftError) setDraftError('');
  };

  /**
   * handleSave
   *
   * Persists the selection and creates a Quick Add node when the form is filled.
   */
  const handleSave = async () => {
    if (isSaving) return;

    const nextSelectedKeys = Array.from(selectedMap.keys());
    const hasDraftFields = hasRelationQuickAddFieldValues(draftFieldValues);
    const wantsCreate = Boolean(trimmedDraftTitle) || hasDraftFields;

    if (!wantsCreate) {
      onChange?.(nextSelectedKeys);
      setOpen(false);
      return;
    }

    if (!canQuickAdd) {
      setDraftError(`You do not have permission to add a new ${String(singularLabel || 'item').toLowerCase()}.`);
      return;
    }

    if (!trimmedDraftTitle) {
      setDraftError(`Enter a ${String(singularLabel || 'item').toLowerCase()} title.`);
      return;
    }

    if (typeof onChange !== 'function') {
      setSaveError('This field cannot be updated right now.');
      return;
    }

    if (!baseKey) {
      setSaveError('No active space.');
      return;
    }

    if (!targetType) {
      setSaveError('No list type configured.');
      return;
    }

    if (!user?.key) {
      setSaveError('You must be signed in to create new items.');
      return;
    }

    setIsSaving(true);
    setDraftError('');
    setSaveError('');

    let createdKey = '';

    try {
      const nodeModule = node?.key
        ? resolveNodeModule(node)
        : resolveCreateNodeModule({ consume: true });
      let newNode = await createNodeFromType(
        targetType,
        user,
        baseKey,
        { module: nodeModule },
      );
      newNode.title = trimmedDraftTitle;
      newNode = applyRelationQuickAddValues(
        newNode,
        displayFieldDefs,
        draftFieldValues,
        targetType,
      );

      createdKey = String(newNode.key || '').trim();
      await createNodeDoc(newNode.key, newNode);

      onChange([...nextSelectedKeys, createdKey].filter(Boolean));
      setOpen(false);
    } catch (error) {
      if (createdKey) {
        try {
          await deleteNodes([createdKey]);
        } catch (cleanupError) {
          console.error('[NodeNodelistModal] Failed to roll back draft node creation:', cleanupError);
        }
      }

      console.error('[NodeNodelistModal] Failed to create draft node:', error);
      setSaveError(`Could not save new ${String(singularLabel || 'item').toLowerCase()}: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ----- RENDER ----- //

  return (
    <Modal
      title={fieldLabel || 'Select Items'}
      isOpen={isOpen}
      width={isMobile ? '100%' : '560px'}
      height="auto"
      maxHeight="min(90vh, 90dvh)"
      iconKey="format_list_bulleted"
      onClose={() => {
        if (isSaving) return;
        setOpen(false);
      }}
    >
      <ModalForm>
        <div className="node-nodelist-modal-container">

          {/* LIST */}
          <div className="node-nodelist-modal-list">

            {/* EMPTY */}
            {!baseKey && (
              <div className="node-nodelist-modal-empty">
                No active base.
              </div>
            )}

            {/* EMPTY */}
            {baseKey && !targetType && (
              <div className="node-nodelist-modal-empty">
                No list type configured.
              </div>
            )}

            {/* QUICK ADD */}
            {baseKey && targetType && canQuickAdd ? (
              <div
                className={[
                  'node-nodelist-modal-row',
                  'node-nodelist-modal-row-draft',
                  quickAddExpanded ? 'is-expanded' : '',
                ].filter(Boolean).join(' ')}
                onClick={quickAddExpanded ? undefined : handleExpandQuickAdd}
              >
                <Checkbox 
                  value={Boolean(trimmedDraftTitle)} 
                  readOnly 
                  size={20}
                  />

                {quickAddExpanded ? (
                  <RelationQuickAddFields
                    singularLabel={singularLabel}
                    title={draftTitle}
                    titleError={draftError}
                    fieldDefs={displayFieldDefs}
                    values={draftFieldValues}
                    disabled={isSaving}
                    autoFocus
                    onTitleChange={(nextTitle) => {
                      setDraftTitle(nextTitle);
                      if (draftError) setDraftError('');
                    }}
                    onFieldChange={handleDraftFieldChange}
                  />
                ) : (
                  <div
                    className="node-nodelist-modal-draft-collapsed"
                    style={{ color: theme.foregroundMuted }}
                  >
                    {quickAddCollapsedLabel}
                  </div>
                )}
              </div>
            ) : null}

            {/* EMPTY */}
            {baseKey && targetType && sortedNodes.length === 0 && !canQuickAdd && (
              <div className="node-nodelist-modal-empty">
                No items
              </div>
            )}

            {baseKey && targetType && sortedNodes.map((item) => {
              const key = String(item?.key || '').trim();
              const checked = selectedMap.has(key);
              const displayLines = getRelationDisplayLines(
                item,
                normalizedDisplayFields,
                targetSchemaFields,
                item?.type || targetType,
              );

              return (
                <div
                  className="node-nodelist-modal-row"
                  key={key}
                  onClick={() => handleToggle(item)}
                >
                  <Checkbox value={checked} readOnly size={20} />

                  {/* META */}
                  <div className="node-nodelist-modal-meta">

                    {/* NAME */}
                    <div className="node-nodelist-modal-name">
                      {getNodeTitle(item)}
                    </div>

                    {displayLines.length > 0 ? (
                      <div className="node-nodelist-modal-display-lines">
                        {displayLines.map((line) => (
                          <div
                            className="node-nodelist-modal-display-line"
                            key={line.key}
                            style={{ color: theme.foregroundMuted }}
                          >
                            {line.text}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ERROR */}
          {saveError && (
            <div
              className="node-nodelist-modal-error"
              style={{
                color: theme.warning || theme.red || '#ff7f7f',
              }}
            >
              {saveError}
            </div>
          )}
        </div>
      </ModalForm>

      <ModalButtons>
        <Button
          label="Cancel"
          enabled={!isSaving}
          onClick={() => setOpen(false)}
        />
        <Button
          iconKey="check"
          label={isSaving ? 'Saving...' : 'Save'}
          enabled={!isSaving}
          onClick={handleSave}
        />
      </ModalButtons>

    </Modal>
  );
};

// Dev Tools
NodeNodelistModal.displayName = 'NodeNodelistModal';

export default NodeNodelistModal;
